import fetch from '../utils/fetch';
import { rosterURL } from 'endpoints';
const RosterSdk = require( '@nackle/roster-sdk' );
import { LABELS } from '../constants';

const searchByProperties = [
    // 'email',
    'firstName',
    'lastName',
    'username',
    'completeName'
];

function RosterService( accessToken, companyId ) {
     const fetchWithAuth = function ( url, options = {} ) {
        options = {
            ...options,
            headers: {
                ...( options.headers || {} ),
                authorization: `Bearer ${ accessToken }`,
                'company-id': `${ companyId }`

            },

        };
        return fetch( url, options );
    };
    
    const sdk = new RosterSdk( rosterURL, fetchWithAuth, undefined, undefined, { error: () => false } );

    const personProperties = [
        sdk.personProperties.emails,
        sdk.personProperties.phones,
        sdk.personProperties.addresses,
        sdk.personProperties.nodes,
        sdk.personProperties.attributes
    ];

    const searchPersons = async ( { searchInput, hierarchyNodeId, status, pageSize, pageNumber, searchModifier, sortedOn, hierarchyId } ) => {

        const searchTerm = searchInput ? {
            genericSearch: {
                searchFields: searchByProperties,
                searchText: searchInput
            }
        } : {};

        return await sdk.searchPersons( {
            personProperties: [
                sdk.personProperties.emails,
                sdk.personProperties.nodes
            ],
            ...searchTerm,
            hierarchyNodeId,
            hierarchyId,
            status,
            pageNumber,
            pageSize,
            sortedOn,
            ...searchModifier
        } );
    };

    const advancedSearchPersons = async ( { searchParams, pageSize, pageNumber, searchModifier } ) => {
        return await sdk.searchPersons( {
            personProperties,
            ...searchParams,
            pageSize,
            pageNumber,
            ...searchModifier
        } );
    };

    const fullSearchPersons = async ( { searchInput, hierarchyNodeId, status, searchModifier } ) => {

        const searchTerm = searchInput ? {
            genericSearch: {
                searchFields: searchByProperties,
                searchText: searchInput.trim()
            }
        } : {};

        return await sdk.fullPersonsSearch( {
            personProperties: [
                sdk.personProperties.emails,
                sdk.personProperties.nodes
            ],
            ...searchTerm,
            ...searchModifier,
            hierarchyNodeId,
            status
        } );
    };

    const fullAdvancedSearchPersons = async ( { searchParams, searchModifier } ) => {
        return await sdk.fullPersonsSearch( {
            personProperties,
            ...searchParams,
            ...searchModifier
        } );
    };

    async function isDigsImplementation( companyId ) {
        try {
            // TODO: roster-proxy needs to get updated to handle CORS
            return await sdk.isDigsImplementation( companyId );
        } catch {
            return true;
        }
    }

    async function listHierarchies( ) {
        const { hierarchies } = await sdk.getHierarchies();
        return hierarchies;
    }

    async function getPersonAttributeTypes() {
        const { attributes } = await sdk.getPersonAttributeTypes();
        return attributes;
    }
    
    async function getPersonExternal( externalID ) {
        return await sdk.getPersonExternal( externalID );
    }

    async function createPersonAttributeType( attributes ) {
        return await sdk.postPersonAttributeTypes( attributes );
    }

    async function getHierarchyById( hierarchyId ) {
        return await sdk.getHierarchy( hierarchyId );
    }

    async function getGroup( groupId ) {
        return await sdk.getGroup( groupId );
    }

    async function getGroups() {
        return await sdk.getGroups();
    }

    async function createFixedGroup( groupName ) {
        const group = {
            name: groupName,
            groupPublic: true,
            personIds: []
        };
        return await sdk.postFixedGroup( group );
    }

    const createHierarchy = async ( hierarchyName ) => {
        return await sdk.postHierarchy( {
            name: hierarchyName
        } );
    };

    const updatePrimaryHierarchy = async ( { id, name, primary } ) => {
        return await sdk.putHierarchy( {
            id,
            name,
            primary
        } );
    };

    const getHierarchyNodes = async ( hierarchyId ) => {
        const { hierarchies } = await sdk.getHierarchyNodes( hierarchyId );
        return hierarchies;
    };

    const getHierarchyNodePersonCount = async ( hierarchyId, hierarchyNodeId, opts = {} ) => {
        const res = await sdk.searchPersons( {
            status: 'active',
            hierarchyNodeId,
            hierarchyId,
            pageSize: 1,
            pageNumber: 1
        }, opts );
        // use the returned total record count because getHierarchyNode can 500 if the node has too many people
        return res.recordCount;
    };

    const getHierarchyNodeRoleTypes = async () => {
        return await sdk.getHierarchyNodeRoleTypes();
    };

    const getHierarchyNodeAttributeTypeList = async () => {
        const { attributes } = await sdk.getHierarchyNodeAttributeTypes();
        return attributes;
    };

    const getHierarchyNodeAttributeTypes = async() => {
        const { attributes } = await sdk.getHierarchyNodeAttributeTypes();
        return attributes;
    };

    const postHierarchyNodeAttributeType = async( attribute ) => {
        const response = await sdk.postHierarchyNodeAttributeType( attribute );

        return response;
    };

    const getHierarchyNodeAttributeValueList = async ( hierarchyId, nodeAttributeId ) => {
        const { distinctValues } = await sdk.getDistinctHierarchyNodeAttributeValues( hierarchyId, nodeAttributeId );
        return distinctValues;
    };

    const getPersonDetailed = async ( personId ) => {
        return await sdk.getPersonDetailed( personId, { personProperties } );
    };

    const getAttributeValue = ( attributes, attributeName ) => {
        return sdk.getAttributeValue( attributes, attributeName );
    };

    const findAttributeTypeByName = ( attributes, name ) => {
        const requestedAttribute = attributes.find( attribute => attribute.name === name );
        if ( !requestedAttribute ) {
            console.error( `Requested attribute ${ name } was not not found in attribute definitions` );
        }
        return requestedAttribute;
    };

    const getPersonAttributeValueList = async ( attributeId ) => {
        const { distinctValues } = await sdk.getDistinctPersonAttributeValues( attributeId );
        return distinctValues;
    };

    const getLanguageFromLocale = ( personLocale, localeList ) => {
        const language = localeList.find( locale => locale.code === personLocale );
        return language ? language.label : null;
    };

    const savePerson = async ( person ) => {
        const personToUpdate = await sdk.getPerson( person.id );
        const updates = {
            ...personToUpdate,
            ...person
        };
        return await sdk.putPerson( person.id, updates );
    };

    const updatePersonAttribute = async ( personId, attributeName, attributeValue ) => {
        return await sdk.putPersonAttribute( personId, attributeName, attributeValue );
    };

    const createPersonAttribute = async ( personId, attributeName, attributeValue ) => {
        return await sdk.postPersonAttribute( personId, { name: attributeName, value: attributeValue } );
    };

    const deletePersonAttribute = async ( personId, attributeName ) => {
        return await sdk.deletePersonAttribute( personId, attributeName );
    };

    const getPrimaryEmailAddress = ( personEmails ) => {
        return sdk.getPrimaryEmailAddressFromSet( personEmails );
    };

    const updatePersonEmailAddress = async ( personId, emailId, address ) => {
        return await sdk.putPersonEmailAddress( personId, emailId, address );
    };

    const createPersonEmailAddress = async ( personId, address ) => {
        return await sdk.postPersonEmailAddress( personId, address );
    };

    const createPersonPostalAddress = async ( personId, address ) => {
        return await sdk.postPersonPostalAddress( personId, address );
    };

    const updatePersonPostalAddress = async ( personId, address ) => {
        return await sdk.putPersonPostalAddress( personId, address.id, address );
    };

    const deletePersonPostalAddress = async ( personId, postalAddressId ) => {
        return await sdk.deletePersonPostalAddress( personId, postalAddressId );
    };

    const addPersonToHierarchyNode = async ( personId, hierarchyNodeId, roleType ) => {
        return await sdk.addPersonToHierarchyNode( personId, hierarchyNodeId, roleType );
    };

    const removePersonFromHierarchyNode = async ( personId, hierarchyNodeId ) => {
        return await sdk.removePersonFromHierarchyNode( personId, hierarchyNodeId );
    };

    const searchPersonsByNodeId = async ( hierarchyNodeId, pageNumber, pageSize = 10, sortedOn = 'surname', sortedBy ) => {
        const response = await sdk.searchPersons( {
            personProperties: [ sdk.personProperties.nodes ],
            hierarchyNodeId,
            sortedOn,
            sortedBy,
            pageNumber,
            pageSize
        } );
        return response;
    };

    const getEmailLabelTypes = () => {
        return LABELS.EMAIL;
    };

    const getStatusTypes = () => {
        return sdk.getStatusTypes();
    };

    const personAttributeTypeExists = ( attributeTypes, attributeName ) => {
        return attributeTypes.some( attribute => attribute.name?.toLowerCase() === attributeName.toLowerCase() );
    };

    return {
        searchPersons,
        advancedSearchPersons,
        fullSearchPersons,
        fullAdvancedSearchPersons,
        isDigsImplementation,
        listHierarchies,
        getPersonAttributeTypes,
        createPersonAttributeType,
        getHierarchyById,
        createHierarchy,
        updatePrimaryHierarchy,
        getGroup,
        getGroups,
        createFixedGroup,
        getHierarchyNodes,
        // getHierarchyNode,
        getHierarchyNodePersonCount,
        getHierarchyNodeRoleTypes,
        getHierarchyNodeAttributeTypeList,
        getHierarchyNodeAttributeValueList,
        getPersonDetailed,
        getPersonExternal,
        getLanguageFromLocale,
        getAttributeValue,
        savePerson,
        updatePersonAttribute,
        createPersonAttribute,
        deletePersonAttribute,
        getPrimaryEmailAddress,
        createPersonEmailAddress,
        updatePersonEmailAddress,
        createPersonPostalAddress,
        updatePersonPostalAddress,
        deletePersonPostalAddress,
        addPersonToHierarchyNode,
        removePersonFromHierarchyNode,
        searchPersonsByNodeId,
        findAttributeTypeByName,
        getPersonAttributeValueList,
        getEmailLabelTypes,
        getStatusTypes,
        personAttributeTypeExists,
        getHierarchyNodeAttributeTypes,
        postHierarchyNodeAttributeType
    };
}

export default RosterService;